/**
 * Pure data-shaping helpers for the structural-extraction schema builder.
 *
 * These take the in-memory BuiltEntity / BuiltRelationship graph (plus the
 * current ReactFlow node positions) and serialize them into the
 * WizardConfiguration payload the backend expects. Extracted out of
 * StructuralExtractionModal so the (previously duplicated) save logic lives in
 * one tested-in-isolation place.
 */

import type { BuiltEntity, BuiltRelationship } from '@/components/lake/extraction-modal/types'
import type {
  AnchorNodeConfig,
  WizardConfiguration,
  WizardEntityConfig,
  WizardGraphEdge,
  WizardGraphNode,
  WizardRelationshipConfig,
} from '@/services/api/cases'

/** A minimal node-position lookup, satisfied by ReactFlow nodes. */
export interface NodePosition {
  x: number
  y: number
}

export interface BuildWizardConfigArgs {
  entities: BuiltEntity[]
  relationships: BuiltRelationship[]
  availableColumns: string[]
  /** Current ReactFlow node positions, keyed by entity id. */
  nodePositions: Map<string, NodePosition>
}

/**
 * Validate the schema graph before saving. Returns an error message to surface
 * to the user, or null when the graph is valid.
 */
export function validateSchemaGraph(
  entities: BuiltEntity[],
  relationships: BuiltRelationship[]
): string | null {
  if (entities.length === 0) {
    return 'Add at least one entity before saving'
  }

  const anchorEntity = entities.find(e => e.isAnchor)
  if (anchorEntity) {
    const hasRelationship = relationships.some(
      rel => rel.sourceEntityId === anchorEntity.id || rel.targetEntityId === anchorEntity.id
    )
    if (!hasRelationship) {
      return 'Anchor node must have at least one relationship to connect to the knowledge graph'
    }
    if (!anchorEntity.entityType) {
      return 'Anchor node must have an entity type selected'
    }
  }

  return null
}

/** Serialize the in-memory schema graph into the backend WizardConfiguration. */
export function buildWizardConfig({
  entities,
  relationships,
  availableColumns,
  nodePositions,
}: BuildWizardConfigArgs): WizardConfiguration {
  const wizardEntities: WizardEntityConfig[] = entities.map(ent => ({
    entity_type: ent.entityType,
    label: ent.label,
    columns: Object.values(ent.columnMappings),
    identifier_column: ent.identifierColumn,
    properties_mapping: ent.columnMappings,
    static_properties: {},
    color: ent.color,
    row_condition: ent.rowCondition ?? null,
  }))

  const wizardRels: WizardRelationshipConfig[] = relationships.map(rel => {
    const sourceEnt = entities.find(e => e.id === rel.sourceEntityId)
    const targetEnt = entities.find(e => e.id === rel.targetEntityId)
    return {
      relationship_type: rel.relationshipType,
      source_entity: sourceEnt?.label || '',
      target_entity: targetEnt?.label || '',
      source_entity_label: sourceEnt?.label,
      target_entity_label: targetEnt?.label,
      label: rel.relationshipType,
      properties_mapping: rel.columnMappings,
      static_properties: {},
      color: rel.color,
    }
  })

  const graphNodes: WizardGraphNode[] = entities.map(ent => {
    const pos = nodePositions.get(ent.id)
    return {
      id: ent.id,
      type: 'entity',
      label: ent.label,
      entity_type: ent.entityType,
      color: ent.color,
      is_anchor: ent.isAnchor ?? false,
      x: pos?.x,
      y: pos?.y,
    }
  })

  const graphEdges: WizardGraphEdge[] = relationships.map(rel => ({
    id: rel.id,
    source: rel.sourceEntityId,
    target: rel.targetEntityId,
    label: rel.relationshipType,
    relationship_type: rel.relationshipType,
    color: rel.color,
  }))

  const anchorEntity = entities.find(e => e.isAnchor)
  let anchorNodeConfig: AnchorNodeConfig | null = null
  if (anchorEntity) {
    const anchorRelationship = relationships.find(
      rel => rel.sourceEntityId === anchorEntity.id || rel.targetEntityId === anchorEntity.id
    )
    anchorNodeConfig = {
      entity_type: anchorEntity.entityType,
      properties: {},
      role: anchorRelationship?.sourceEntityId === anchorEntity.id ? 'source' : 'target',
      edge_type: anchorRelationship?.relationshipType || '',
      edge_properties_columns: anchorRelationship?.columnMappings || {},
    }
  }

  return {
    name: 'Manual Schema',
    description: 'User-built extraction schema',
    data_pattern: 'tabular',
    row_model: 'per-row',
    suggested_pipeline: 'tabular_extraction',
    entities: wizardEntities,
    relationships: wizardRels,
    unmapped_columns: availableColumns.filter(
      col => !entities.some(e => Object.values(e.columnMappings).includes(col))
    ),
    graph_nodes: graphNodes,
    graph_edges: graphEdges,
    anchor_node: anchorNodeConfig,
    confidence: 1.0,
    explanation: 'Schema built manually via Schema Builder UI',
  }
}

/** Build the enriched scope_note for the create-mode "all" scope save. */
export function buildScopeNote(scopeNote: string, rowCount: number, colCount: number): string {
  const rowSummary = rowCount ? `${rowCount} row${rowCount === 1 ? '' : 's'}` : 'whole document'
  const colSummary = colCount ? `${colCount} col${colCount === 1 ? '' : 's'}` : 'all columns'
  return scopeNote.trim()
    ? `${scopeNote.trim()} (selected ${rowSummary}, ${colSummary})`
    : `Imported tabular (${rowSummary}, ${colSummary})`
}
